import { ReactNode, useDeferredValue, useEffect, useMemo, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";

const CHART_WIDTH = 100;
const CHART_STROKE = 6;
const p = 2 * Math.PI * (CHART_WIDTH/2 - CHART_STROKE/2);
const getValueP = (v: number) => p * (v / 100);

const CircleChart: FCX<{
    color: string;
    value: number;
    title: ReactNode;
    tooltipId?: string;
    percentileValue: number;
}> = ({
    color,
    value,
    title,
    tooltipId,
    percentileValue,
    className,
    testId,
    ...props
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const deferredValue = useDeferredValue(value);

    // Handle mounting animation (0 to initial value)
    useEffect(() => {
        const timer = setTimeout(() => setIsMounted(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const circleDasharray = useMemo(() => {
        const displayValue = isMounted ? deferredValue : 0;
        return `${getValueP(displayValue)}, ${p - getValueP(displayValue)}`
    }, [deferredValue, isMounted]);

    return (
        <div
            className={cn("CircleChart", className)}
            data-testid={testId}
            {...props}
        >
            <svg
                className="CircleChart__line"
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_WIDTH}`} width={CHART_WIDTH} height={CHART_WIDTH}
            >
                <circle
                    cx={CHART_WIDTH / 2}
                    cy={CHART_WIDTH / 2}
                    r={CHART_WIDTH / 2 - CHART_STROKE / 2}
                    fill="none"
                    strokeWidth={CHART_STROKE}
                    stroke={color}
                    strokeLinecap={"round"}
                    strokeDasharray={circleDasharray}
                    style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
                />
            </svg>
            <div
                className={cn(
                    "CircleChart__sub-value",
                    "cl-secondary",
                    "appears",
                    "fast",
                    "del-3",
                    tooltipId && 'is-clickable',
                )}
                data-tooltip-id={tooltipId}
                data-testid="textCircleChartSubValue"
            >
                {percentileValue}
            </div>
            <div className="CircleChart__description">
                <div
                    className={cn(
                        "CircleChart__value",
                        "appears",
                        "fast",
                        "del-1",
                    )}
                    data-testid="textCircleChartValue"
                >
                    {value}<sub>%</sub>
                </div>
                <div
                    className={cn(
                        "CircleChart__title",
                        "appears",
                        "fast",
                        "del-2",
                    )}
                    data-testid="textCircleChartTitle"
                >
                    {title}
                </div>
            </div>
        </div>
    );
};

export default CircleChart;